// "Money story" panel — replaces the right Inspector when nothing's selected
// and there's BOM data to show. The headline is BEAD economics: pencils Y/N,
// cost per eligible passing, layer 2 uplift.
//
// This is Beat 3 of the demo. Keep it big, bold, and instantly readable.

import type { BomResult } from '../types/bom';

const fmtUSD = (n: number) =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M`
  : n >= 1_000   ? `$${(n / 1_000).toFixed(0)}k`
  : `$${Math.round(n).toLocaleString()}`;
const fmtFt  = (n: number) => `${Math.round(n).toLocaleString()} ft`;
const fmtNum = (n: number) => Math.round(n).toLocaleString();

export default function BomPanel({ bom, busy }: { bom: BomResult | null; busy?: boolean }) {
  if (!bom) {
    return (
      <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
        {busy ? 'Computing BOM…' : 'Run auto-design or place segments to see the cost rollup.'}
      </div>
    );
  }

  const pencils = bom.pencils_against_subgrant;
  const hasSubgrant = bom.subgrant_amount > 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* BEAD HEADLINE */}
      {hasSubgrant && (
        <div className={`bom-pencils ${pencils ? 'pencils-yes' : 'pencils-no'}`}>
          <div className="bom-pencils__icon">{pencils ? '✓' : '✗'}</div>
          <div style={{ flex: 1 }}>
            <div className="bom-pencils__headline">
              {pencils ? 'Project pencils' : 'Project does not pencil'}
            </div>
            <div className="bom-pencils__sub">
              {pencils
                ? `${fmtUSD(bom.delta)} headroom against subgrant + ${(bom.contingency_pct * 100).toFixed(0)}%`
                : `${fmtUSD(-bom.delta)} OVER budget — need to redesign`}
            </div>
          </div>
        </div>
      )}

      {/* THREE BIG TILES */}
      <div className="bom-grid">
        <Tile label="Total cost" value={fmtUSD(bom.total_cost)} />
        <Tile label="Cost / eligible BSL" value={fmtUSD(bom.cost_per_eligible_passing)}
              accent={bom.cost_per_eligible_passing < 3000 ? 'success' : 'warning'} />
        <Tile label="Cost / total passing" value={fmtUSD(bom.cost_per_passing)} accent="info" />
      </div>

      {/* PASSINGS BREAKDOWN */}
      <div className="bom-section">
        <div className="bom-section__title">Passings @ {bom.passing_buffer_ft} ft buffer</div>
        <div className="bom-row">
          <span className="bom-dot bom-dot--bead" />
          <span style={{ flex: 1 }}>BEAD eligible (Layer 1)</span>
          <b>{fmtNum(bom.passings_eligible)}</b>
        </div>
        <div className="bom-row">
          <span className="bom-dot bom-dot--opportunistic" />
          <span style={{ flex: 1 }}>Opportunistic uplift (Layer 2)</span>
          <b>{fmtNum(bom.passings_opportunistic)}</b>
        </div>
        <div className="bom-row bom-row--total">
          <span style={{ flex: 1 }}>Total passings</span>
          <b>{fmtNum(bom.passings_total)}</b>
        </div>
      </div>

      {/* SUBGRANT BAR (if BEAD project) */}
      {hasSubgrant && (
        <div className="bom-section">
          <div className="bom-section__title">Subgrant utilization</div>
          <div className="bom-bar">
            <div
              className="bom-bar__fill"
              style={{
                width: `${Math.min(100, (bom.total_cost / bom.budget_total) * 100)}%`,
                background: pencils ? '#10b981' : '#ef4444',
              }}
            />
          </div>
          <div className="bom-bar__labels">
            <span>{fmtUSD(bom.total_cost)} spent</span>
            <span style={{ color: 'var(--text-muted)' }}>/ {fmtUSD(bom.budget_total)} budget</span>
          </div>
        </div>
      )}

      {/* COST BREAKDOWN */}
      <div className="bom-section">
        <div className="bom-section__title">Cost breakdown</div>
        <CostRow label="Cable" sub={fmtFt(bom.total_cable_ft)} value={bom.total_cable_cost} />
        <CostRow label="Hardware" sub={`${bom.nodes.reduce((s, n) => s + n.count, 0)} nodes`} value={bom.hardware_cost} />
        <CostRow label="Labor" value={bom.labor_cost} />
        <CostRow label="Adders" sub="eng + permits + PM + contingency" value={bom.adders_cost} />
        <CostRow label="Total" value={bom.total_cost} bold />
      </div>

      {/* CABLE DETAIL */}
      <div className="bom-section">
        <div className="bom-section__title">Cable detail</div>
        <DetailRow label="Feeder"       ft={bom.feeder_ft}       cost={bom.feeder_cost} />
        <DetailRow label="Distribution" ft={bom.distribution_ft} cost={bom.distribution_cost} />
        <DetailRow label="Drop"         ft={bom.drop_ft}         cost={bom.drop_cost} />
        <DetailRow label="Slack"        ft={bom.slack_ft}        cost={bom.slack_cost} muted />
      </div>

      {/* HARDWARE DETAIL */}
      {bom.nodes.length > 0 && (
        <div className="bom-section">
          <div className="bom-section__title">Hardware detail</div>
          {bom.nodes.map((n) => (
            <DetailRow
              key={n.type}
              label={n.type.replace('_', ' ')}
              ft={null}
              cost={n.cost}
              extra={`${n.count} × ${fmtUSD(n.unit_cost)}`}
            />
          ))}
        </div>
      )}

      <div style={{ fontSize: 10, color: 'var(--text-subtle)', marginTop: 4 }}>
        Recomputes when you edit segments or change Plant Settings.
      </div>
    </div>
  );
}

function Tile({ label, value, accent }: { label: string; value: string; accent?: 'success' | 'warning' | 'info' }) {
  const accentColor =
    accent === 'success' ? '#10b981' :
    accent === 'warning' ? '#f59e0b' :
    accent === 'info'    ? '#0ea5e9' : 'var(--text)';
  return (
    <div className="bom-tile">
      <div className="bom-tile__label">{label}</div>
      <div className="bom-tile__value" style={{ color: accentColor }}>{value}</div>
    </div>
  );
}

function CostRow({ label, sub, value, bold }: { label: string; sub?: string; value: number; bold?: boolean }) {
  return (
    <div className={`bom-row ${bold ? 'bom-row--total' : ''}`}>
      <span style={{ flex: 1 }}>
        <span style={{ fontWeight: bold ? 700 : 500 }}>{label}</span>
        {sub && <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--text-muted)' }}>{sub}</span>}
      </span>
      <b>{value > 0 ? `$${Math.round(value).toLocaleString()}` : '—'}</b>
    </div>
  );
}

function DetailRow({ label, ft, cost, extra, muted }: { label: string; ft: number | null; cost: number; extra?: string; muted?: boolean }) {
  return (
    <div className="bom-row" style={{ opacity: muted ? 0.7 : 1 }}>
      <span style={{ flex: 1, textTransform: 'capitalize' }}>{label}</span>
      {ft !== null && <span style={{ color: 'var(--text-muted)', fontSize: 11, marginRight: 6 }}>{Math.round(ft).toLocaleString()} ft</span>}
      {extra && <span style={{ color: 'var(--text-muted)', fontSize: 11, marginRight: 6 }}>{extra}</span>}
      <b>{cost > 0 ? `$${Math.round(cost).toLocaleString()}` : '—'}</b>
    </div>
  );
}
